// InfoTab.jsx
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  getCategoryDetailsById,
  updateCategory,
} from '../../../services/AdminApis'; // Assuming this path is correct

// THE ImageUploader component from the file you provided
import ImageUploader from '../../../components/ImageUploader';
import { uploadFileToS3 } from '../../../utility/S3Uploader';
import { api } from "../../../services/apiServices"; // Import the base api for our new calls
// Import shadcn/ui components
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Checkbox } from "../../../components/ui/checkbox";
import { useToast } from "../../../hooks/use-toast"; // Assuming this path is correct
import { InputWithSlug } from '../../../components/ui/input-with-slug'; // Assuming this path is correct
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";

const MemoizedImageUploader = React.memo(ImageUploader);

const industryTypes = [
  { value: "home_services", label: "Home Services" },
  { value: "business_and_professional_services", label: "Business and Professional Services" },
  { value: "education_and_training_services", label: "Education and Training Services" },
  { value: "events_and_hospitality_services", label: "Events and Hospitality Services" },
  { value: "trade_services", label: "Trade Services" },
  { value: "personal_services", label: "Personal Services" },
  { value: "general_services", label: "General Services" },
];

const categoryTypeValues = {
    PARENT: "parent",
    CHILD: "child",
    AVATAR: "avatar"
};

export default function InfoTab({ category: summaryCategory, categoryList, locale }) {
  const { toast } = useToast();

  // Form field states
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [categoryType, setCategoryType] = useState('');
  const [parentId, setParentId] = useState(null);
  const [icon, setIcon] = useState('');
  const [noun, setNoun] = useState('Service');
  const [plural, setPlural] = useState('Services');
  const [image, setImage] = useState('');
  const [maxLeadPurchases, setMaxLeadPurchases] = useState(5);
  const [nationalCategory, setNationalCategory] = useState(false);
  const [localCheckbox, setLocalCheckbox] = useState(true);
  const [industry, setIndustry] = useState('');
  const [isActive, setIsActive] = useState(true);
  const s3IconBaseUrl = process.env.REACT_APP_S3_ASSETS_BASE_URL;
  const [masterCategoryName, setMasterCategoryName] = useState('');
  const [activeLocalizations, setActiveLocalizations] = useState([]);
  const isMasterView = locale === 'master';
  const [defaultChildId, setDefaultChildId] = useState(null);

  //console.log("Locale:",locale)
  //console.log("s3IconBaseUrl:",s3IconBaseUrl)
   // console.log("S3 link:",process.env.REACT_APP_S3_ASSETS_FETCH_URL)

  
  const [initialData, setInitialData] = useState(null);
  const [parentCategories, setParentCategories] = useState(categoryList);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  


  const populateForm = useCallback((data) => {
    setName(data.name || '');
    setSlug(data.slug || '');
    setCategoryType(data.type || '');
    setParentId(data.parent_id || null);
    setDefaultChildId(data.default_child_id || null);
    setIcon(data.icon || '');
    setNoun(data.noun || 'Service');
    setPlural(data.plural || 'Services');
    setImage(data.image || '');
    setMaxLeadPurchases(data.max_lead_purchases === null ? 5 : Number(data.max_lead_purchases));
    setNationalCategory(data.national_category || false);
    setLocalCheckbox(data.local_checkbox === null ? true : data.local_checkbox);
    setIndustry(data.industry || '');
    setIsActive(data.is_active === null ? true : data.is_active);
    setInitialData(data);
  }, []);

  const iconUrl = useMemo(() => {
    // Only construct the URL if the icon name and base URL exist
    if (!icon || !s3IconBaseUrl) {
      return '';
    }
    
    // If user accidentally pastes a full URL, just use it
    if (icon.startsWith('http')) {
        return icon;
    }

    // Otherwise, construct the full URL
    return `${s3IconBaseUrl}/${icon}.svg`;
  }, [icon, s3IconBaseUrl]);

  useEffect(() => {
    const fetchData = async () => {
      if (!summaryCategory?.id) return;
      setIsLoading(true);
      setMasterCategoryName('');
      setActiveLocalizations([]);

      try {
        const [categoryDetailsData] = await Promise.all([
          getCategoryDetailsById(summaryCategory.id,locale)
        ]);
        if (categoryDetailsData) {
          populateForm(categoryDetailsData);
        }
        if (isMasterView) {
            // It's a Master Category, find its active localizations
            try {
              const locRes = await api.get(`/admin/categories/master/${categoryDetailsData.id}/localizations`);
              setActiveLocalizations(locRes.data || []);
            } catch (err) {
              console.error("Failed to fetch localizations", err);
            }
          } else if (categoryDetailsData.master_category_id) {
            // It's a Localized Category, find its Master's name
            try {
              // We must fetch from the 'master' locale to find the master category
              const masterListRes = await api.get("/admin/categories/basic-list", { params: { locale: 'master' } });
              const master = masterListRes.data.find(c => c.id === categoryDetailsData.master_category_id);
              if (master) {
                setMasterCategoryName(master.name);
              }
            } catch (err) {
              console.error("Failed to fetch master category name", err);
            }
          }
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load category information.",
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [summaryCategory?.id, populateForm, toast]);

  useEffect(() => {
    if (!initialData) return;
    const changed =
      name !== (initialData.name || '') ||
      slug !== (initialData.slug || '') ||
      categoryType !== (initialData.type || '') ||
      (icon || '') !== (initialData.icon || '') ||
      (parentId === null ? undefined : Number(parentId)) !== initialData.parent_id ||
      (defaultChildId === null ? undefined : Number(defaultChildId)) !== (initialData.default_child_id || null) ||
      noun !== (initialData.noun || 'Service') ||
      plural !== (initialData.plural || 'Services') ||
      image !== (initialData.image || '') ||
      Number(maxLeadPurchases) !== (initialData.max_lead_purchases === null ? 5 : Number(initialData.max_lead_purchases)) ||
      nationalCategory !== (initialData.national_category || false) ||
      localCheckbox !== (initialData.local_checkbox === null ? true : initialData.local_checkbox) ||
      industry !== (initialData.industry || '') ||
      isActive !== (initialData.is_active === null ? true : initialData.is_active);
    setUnsavedChanges(changed);
  }, [
    name, slug, categoryType, icon, parentId, noun, plural, image,
    maxLeadPurchases, nationalCategory, localCheckbox,   
    industry, isActive, initialData, defaultChildId
  ]);

  useEffect(() => {
    const warnOnLeave = (e) => {
      if (unsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warnOnLeave);
    return () => window.removeEventListener('beforeunload', warnOnLeave);
  }, [unsavedChanges]);

  const handleImageUpload = async (uploadedFile) => { // Changed: `uploadedFiles` to `uploadedFile`
    //const token = localStorage.getItem('selyst_token');
    try {
      const fileToUpload = uploadedFile; // Changed: directly use uploadedFile
      const dynamicPath = summaryCategory.id ? `categories/images` : 'category/uncategorized/images';

      const timestamp = Date.now();
      const fileExt = fileToUpload.name.split('.').pop();
      const fileNameBase = fileToUpload.name.substring(0, fileToUpload.name.lastIndexOf('.'));
      console.log("Filenamebase:",fileNameBase)
      console.log("File extension:",fileExt)
      const sanitizedBase = fileNameBase.replace(/[^a-zA-Z0-9-_]/g, '-');
      console.log("Sanitized base:",sanitizedBase)
      const newFileName = `${sanitizedBase}-${timestamp}.${fileExt}`;
      console.log("New filename:",newFileName)

      const renamedFile = new File([fileToUpload], newFileName, { type: fileToUpload.type });


      if (renamedFile) { // This check is important: only proceed if a file object is provided
        const url = await uploadFileToS3(renamedFile, dynamicPath);
        setImage(url);
        toast({
          title: "Image Uploaded",
          description: "The category image has been successfully uploaded to S3 and linked.",
        });
      } else {
        // Optional: If uploadedFile is null, it means the user cleared a new selection in ImageUploader.
        // You might want to inform the user or clear the `image` state if the intention is to remove the saved image.
        // For now, if no file is provided, no new upload occurs and the existing `image` state (S3 URL) is maintained.
        // If you want clearing selection in ImageUploader to also clear the saved image URL:
        // setImage('');
        // toast({ title: "Selection Cleared", description: "Pending image upload was cleared." });
      }
    } catch (err) {
      console.error('Upload failed', err);
      toast({
        variant: "destructive",
        title: "Upload Failed",
        description: "Image upload failed. Please try again.",
      });
    }
  };

  const handleSaveChanges = async () => {
    if (parentId !== initialData?.parent_id) {
      const confirmedParentChange = window.confirm(
        "⚠️ Changing the parent category may affect hierarchy and other settings. Do you want to proceed?"
      );
      if (!confirmedParentChange) return;
    }

    if (initialData?.type === categoryTypeValues.CHILD && categoryType === categoryTypeValues.PARENT) {
      const confirmedTypeChange = window.confirm(
        "⚠️ Converting this category from Child to Parent can have significant impacts on its relationships, associated questions, and how it functions. This action might also affect its parent-child links. Are you sure you want to proceed?"
      );
      if (!confirmedTypeChange) return;
    }

    setIsSaving(true);
    const getNullableNumber = (value) => {
        const num = Number(value);
        return (value === '' || value === null || isNaN(num)) ? null : num;
    };

    const payload = {
      id: summaryCategory.id,
      name: name,
      slug: slug,
      type: categoryType,
      parent_id: parentId ? Number(parentId) : null,
      default_child_id: defaultChildId ? Number(defaultChildId) : null,
      icon: icon || null,
      noun: noun || null,
      plural: plural || null,
      image: image || null,
      max_lead_purchases: getNullableNumber(maxLeadPurchases),
      national_category: nationalCategory,
      local_checkbox: localCheckbox,
      industry: industry || null,
      is_active: isActive,
    };
   
    try {
      const updatedCategoryData = await updateCategory(summaryCategory.id, locale, payload);
      if (updatedCategoryData) {
        populateForm(updatedCategoryData);
        setInitialData(updatedCategoryData); 
        setUnsavedChanges(false);
        toast({
          title: "Success!",
          description: "Category updated successfully.",
        });
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || "An unknown error occurred while updating.";
      toast({
        variant: "destructive",
        title: "Update Failed",
        description: errorMsg,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const memoizedImageUrl = useMemo(() => (image ? [image] : []), [image]);
  const categoryTypeOptions = useMemo(() => {
    const options = [];
    const currentOriginalType = initialData?.type;

    if (currentOriginalType === categoryTypeValues.CHILD) {
      options.push({ value: categoryTypeValues.CHILD, label: "Child" });
      options.push({ value: categoryTypeValues.PARENT, label: "Parent" });
    } else if (currentOriginalType === categoryTypeValues.PARENT) {
      options.push({ value: categoryTypeValues.PARENT, label: "Parent" });
    } else if (currentOriginalType === categoryTypeValues.AVATAR) {
      options.push({ value: categoryTypeValues.AVATAR, label: "Avatar" });
    } else if (currentOriginalType) { 
        options.push({value: currentOriginalType, label: currentOriginalType.charAt(0).toUpperCase() + currentOriginalType.slice(1)})
    }
    // Fallback if initialData.type is not yet loaded or empty
    if (options.length === 0 && categoryType) {
        options.push({value: categoryType, label: categoryType.charAt(0).toUpperCase() + categoryType.slice(1)})
    } else if (options.length === 0) {
        options.push({ value: categoryTypeValues.CHILD, label: "Child" });
        options.push({ value: categoryTypeValues.PARENT, label: "Parent" });
    }

    return options;
  }, [initialData?.type, categoryType]);

  const availableChildCategories = useMemo(() => {
    if (!categoryList || !summaryCategory?.id) return [];
    return categoryList.filter(cat => cat.parent_id === summaryCategory.id);
  }, [categoryList, summaryCategory?.id]);

  if (isLoading) {
    return (
      <div className="space-y-6 p-1">
        <h2 className="text-xl font-semibold text-foreground">Edit Category Info</h2>
        <div>Loading category information... (Consider using Skeleton components here for better UX)</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-1">
      {/* --- NEW: LOCALIZED-ONLY READ-ONLY FIELDS --- */}
      {!isMasterView && (
        <Card className="mb-6 bg-muted/30">
          <CardHeader>
            <CardTitle>Localization Info (Read-Only)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="masterId">Master Category ID</Label>
                <Input id="masterId" value={initialData?.master_category_id || 'N/A'} disabled />
              </div>
              <div>
                <Label htmlFor="masterName">Master Category Name</Label>
                <Input id="masterName" value={masterCategoryName || (initialData?.master_category_id ? 'Loading...' : 'N/A')} disabled />
              </div>
              <div>
                <Label htmlFor="countryCode">Country Code</Label>
                <Input id="countryCode" value={initialData?.country_code || 'N/A'} disabled />
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      <h2 className="text-xl font-semibold text-foreground">Edit Category Info</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Column 1 */}
        <div className="space-y-4">
          <InputWithSlug
            key={summaryCategory.id} // Ensure key is stable if summaryCategory.id doesn't change, or tied to initialData load
            titleLabel="Category Name"
            slugLabel="Slug"
            titlePlaceholder="e.g., Plumbers, Web Design Services"
            slugPlaceholder="auto-generated-slug"
            initialTitle={name} // Controlled by state `name`
            initialSlug={slug}   // Controlled by state `slug`
            onTitleChange={(newTitle) => setName(newTitle)}
            onSlugChange={(newSlug, _isManuallyEdited) => setSlug(newSlug)}
            titleInputId="categoryName"
            slugInputId="categorySlug"
          />
          
          <div>
            <Label htmlFor="categoryType">Category Type</Label>
             <Select
              value={categoryType}
              onValueChange={setCategoryType}
              // Disable if only one option or if it's an Avatar type (usually not changeable by user)
              disabled={!isMasterView || categoryTypeOptions.length <= 1} // <-- MODIFIED
            >
              <SelectTrigger id="categoryType">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {categoryTypeOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {initialData?.type === categoryTypeValues.CHILD && categoryType === categoryTypeValues.PARENT && (
              <p className="text-sm text-destructive mt-1">
                Warning: You are about to change this category from Child to Parent. This action requires confirmation upon saving.
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="parentCategory">Parent Category</Label>
            <Select
              value={parentId ? String(parentId) : ""}
              onValueChange={value => setParentId(value ? Number(value) : null)}
              disabled={!isMasterView || categoryType === categoryTypeValues.PARENT} // <-- MODIFIED
            >
              <SelectTrigger id="parentCategory">
                <SelectValue placeholder={(categoryType === categoryTypeValues.PARENT) ? "N/A for this type" : "No Parent"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="NONE">No Parent</SelectItem> {/* Explicit option for no parent */}
                {parentCategories
                  .filter(cat => cat.id !== summaryCategory.id && cat.type === 'parent') // Ensure current category isn't its own parent
                  .map(cat => (
                    <SelectItem key={cat.id} value={String(cat.id)}>{cat.name}</SelectItem>
                  ))}
              </SelectContent>
            </Select>
             {categoryType === categoryTypeValues.PARENT && parentId && (
                <p className="text-sm text-destructive mt-1">
                    Parent categories cannot have a parent. This field will be cleared on save if type is Parent.
                </p>            
            )}
          </div>

          {/* 6. INSERT DEFAULT CHILD DROPDOWN */}
          {/* Only show if Master View AND Type is Parent */}
          {isMasterView && categoryType === categoryTypeValues.PARENT && (
            <div>
              <Label htmlFor="defaultChild">Default Child Category</Label>
              <Select
                value={defaultChildId ? String(defaultChildId) : "NONE"}
                onValueChange={(val) => setDefaultChildId(val === "NONE" ? null : Number(val))}
              >
                <SelectTrigger id="defaultChild">
                  <SelectValue placeholder="Select a default child" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">None</SelectItem>
                  {availableChildCategories.map((child) => (
                    <SelectItem key={child.id} value={String(child.id)}>
                      {child.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground mt-1">
                Select which child category serves as the default for this parent.
              </p>
            </div>
          )}

          <div>
            <Label htmlFor="categoryIcon">Icon Name (Lucide or Phosphor)</Label>
            <div className="flex items-center space-x-2">
              <Input
                id="categoryIcon"
                value={icon}
                onChange={e => setIcon(e.target.value)}
                placeholder="e.g., home (Lucide), Horse (Phosphor)"
                className="flex-1"
              />
              {iconUrl && (
              <div className="p-2 border rounded bg-muted flex items-center justify-center h-10 w-10">
                <img
                  src={iconUrl}
                  alt={icon ? `${icon} icon preview` : 'Icon preview'}
                  className="h-6 w-6"
                  // Optional: Add error handling for broken image links
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </div>
            )}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Examples: "check-circle" (Lucide), "House" (Phosphor). Case-sensitive for Phosphor.
              Refer to <a href="https://lucide.dev/icons/" target="_blank" rel="noopener noreferrer" className="underline text-primary">Lucide</a> or 
              <a href="https://phosphoricons.com/" target="_blank" rel="noopener noreferrer" className="underline text-primary"> Phosphor</a>.
            </p>
            <p className="text-xs text-amber-600 mt-1">
                Note: Dynamic Phosphor icons might impact load times if many are used.
            </p>
          </div>
          
          <div>
            <Label htmlFor="categoryNoun">Noun (Singular)</Label>
            <Input id="categoryNoun" value={noun} onChange={e => setNoun(e.target.value)} placeholder="e.g., Plumber, Service" />
            <p className="text-sm text-muted-foreground mt-1">What do you call one instance of this category (e.g., a "Plumber", a "Service Provider")?</p>
          </div>
          <div>
            <Label htmlFor="categoryPlural">Plural</Label>
            <Input id="categoryPlural" value={plural} onChange={e => setPlural(e.target.value)} placeholder="e.g., Plumbers, Services" />
             <p className="text-sm text-muted-foreground mt-1">What do you call multiple instances (e.g., "Plumbers", "Services")?</p>
          </div>
           <div>
            <Label htmlFor="industry">Industry</Label>
            <Select value={industry} onValueChange={setIndustry} disabled={!isMasterView}>
              <SelectTrigger id="industry">
                <SelectValue placeholder="Select industry" />
              </SelectTrigger>
              <SelectContent>
                {industryTypes.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Column 2 */}
        <div className="space-y-4">
          <MemoizedImageUploader
            label="Category Image"
            name="categoryImage"
            initialUrls={memoizedImageUrl}
            multiple={false}
            onChange={handleImageUpload}
            note="Previews new selections. Upload to S3 happens on selection. Max 5MB."
            fileType="image/*"
            maxSizeMB={5} // Consistent with ImageUploader default
          />
          <div>
            <Label htmlFor="maxLeadPurchases">Max Lead Purchases</Label>
            <Input id="maxLeadPurchases" type="number" value={maxLeadPurchases} onChange={e => setMaxLeadPurchases(e.target.value)} />
             <p className="text-sm text-muted-foreground mt-1">Default max times a lead in this category can be purchased.</p>
          </div>
          <div className="space-y-3 pt-2">
            <div className="flex items-center space-x-2">
              <Checkbox id="nationalCategory" checked={nationalCategory} onCheckedChange={setNationalCategory} />
              <Label htmlFor="nationalCategory" className="font-normal">National Category</Label>
            </div>
             <p className="text-sm text-muted-foreground -mt-2 pl-6">Check if services can be offered nationwide.</p>
            <div className="flex items-center space-x-2">
              <Checkbox id="localCheckbox" checked={localCheckbox} onCheckedChange={setLocalCheckbox} />
              <Label htmlFor="localCheckbox" className="font-normal">Enable Local Targeting</Label>
            </div>
            <p className="text-sm text-muted-foreground -mt-2 pl-6">If checked, users can specify local service areas.</p>
            <div className="flex items-center space-x-2">
              <Checkbox id="isActive" checked={isActive} onCheckedChange={setIsActive} />
              <Label htmlFor="isActive" className="font-normal">Category is Active</Label>
            </div>
            <p className="text-sm text-muted-foreground -mt-2 pl-6">Inactive categories won't be shown to users.</p>
          </div>
        </div>
      </div>
      {isMasterView && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Active Localizations</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p>Loading...</p>
            ) : activeLocalizations.length > 0 ? (
              <ul className="list-disc pl-5 space-y-1">
                {activeLocalizations.map(loc => (
                  <li key={loc.id}>
                    <strong>{loc.country_code}</strong>: {loc.name} (ID: {loc.id})
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground">This master category is not active in any countries.</p>
            )}
          </CardContent>
        </Card>
      )}

      <div className="mt-8 flex justify-end">
        <Button
          onClick={handleSaveChanges}
          disabled={isSaving || !unsavedChanges}
          variant="default" // [cite: 10]
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}